export class NormalizedSet<TValue>
	implements Set<TValue> {
	private readonly _internalStore: Map<string, TValue>;
	private readonly _valueNormalizer: (value: TValue) => string;
	public readonly [Symbol.toStringTag]: string = "NormalizedSet";

	constructor(
		valueNormalizer: (value: TValue) => string,
		iterable?: Iterable<TValue>
	) {
		this._valueNormalizer = valueNormalizer;
		this._internalStore = new Map();

		if (iterable) {
			for (const element of iterable) {
				this._internalStore.set(
					valueNormalizer(element),
					element
				);
			}
		}
	}

	public add(value: TValue): this {
		const key: string = this._valueNormalizer(value);

		this._internalStore.set(key, value);

		return this;
	}

	public clear(): void {
		this._internalStore.clear();
	}

	public delete(value: TValue): boolean {
		const key: string = this._valueNormalizer(value);

		return this._internalStore.delete(key);
	}

	public forEach(
		callbackfn: (value: TValue, value2: TValue, set: Set<TValue>) => void,
		thisArg?: any
	): void {
		this._internalStore.forEach(
			(value: TValue) => callbackfn(value, value, this),
			thisArg
		);
	}

	public has(value: TValue): boolean {
		const key: string = this._valueNormalizer(value);
		return this._internalStore.has(key);
	}

	public get size(): number {
		return this._internalStore.size;
	}

	public entries(): IterableIterator<[TValue, TValue]> {
		return new EntriesIterator(this._internalStore.values());
	}

	public keys(): IterableIterator<TValue> {
		return this._internalStore.values();
	}

	public values(): IterableIterator<TValue> {
		return this._internalStore.values();
	}

	public [Symbol.iterator](): IterableIterator<TValue> {
		return this._internalStore.values();
	}
}

class EntriesIterator<TValue> implements IterableIterator<[TValue, TValue]> {
	public readonly return?: (value?: undefined) => IteratorResult<[TValue, TValue], undefined>;
	public readonly throw?: (e?: any) => IteratorResult<[TValue, TValue], undefined>;
	private readonly _internalStoreIterator: IterableIterator<TValue>;

	constructor(internalStoreIterator: IterableIterator<TValue>) {
		this._internalStoreIterator = internalStoreIterator;

		const internalReturn: ((value?: undefined) => IteratorResult<TValue, undefined>) | undefined = internalStoreIterator.return;

		if (internalReturn) {
			this.return = (value?: undefined): IteratorResult<[TValue, TValue], undefined> => {
				const internalResult: IteratorResult<TValue, undefined> = internalReturn(value);
				return convertResult(internalResult);
			};
		}

		const internalThrow: ((e?: any) => IteratorResult<TValue, undefined>) | undefined = internalStoreIterator.throw;

		if (internalThrow) {
			this.throw = (e?: any): IteratorResult<[TValue, TValue], undefined> => {
				const internalResult: IteratorResult<TValue, undefined> = internalThrow(e);

				return convertResult(internalResult);
			};
		}
	}

	public [Symbol.iterator](): IterableIterator<[TValue, TValue]> {
		return this;
	}

	public next(...args: [] | [undefined]): IteratorResult<[TValue, TValue], undefined> {
		const internalResult: IteratorResult<TValue, undefined> = this._internalStoreIterator.next(
			...args
		);

		return convertResult(internalResult);
	}
}

function convertResult<TValue>(internalResult: IteratorResult<TValue, undefined>): IteratorResult<[TValue, TValue], undefined> {
	if (internalResult.done) {
		return {
			done: true,
			value: undefined
		};
	}

	return {
		done: internalResult.done,
		value: [internalResult.value, internalResult.value]
	};
}